<template>
    <DCard
        :title="__('settings.orders.order_clients.Webglobe')"
        padding
    >
        <DFormRow
            :label="__('settings.order_client.form.order_client')"
            :hint="__('settings.order_client.form.order_client_hint_webglobe')"
        >
            <DSwitch
                v-model="form.enabled"
                color="success"
                variant="3d"
                @update:modelValue="$emit('webglobe-valid', !form.enabled || credentialsAreValid)"
            />
        </DFormRow>
        <DRow v-if="form.enabled">
            <DFormRow
                :label="__('settings.order_client.form.api_url')"
                :is-required="true"
                :error="errorsData.api_url ?? errorsData['Webglobe.api_url']"
                :hint="__('settings.order_client.form.api_url_hint_webglobe')"
            >
                <DInput
                    id="api_url"
                    v-model="form.api_url"
                    :placeholder="__('settings.order_client.form.api_url')"
                    :is-valid="isFormFieldValid('api_url')"
                    type="text"
                />
            </DFormRow>
            <DFormRow
                :label="__('settings.order_client.form.username')"
                :is-required="true"
                :error="errorsData.username ?? errorsData['Webglobe.username']"
                :hint="__('settings.order_client.form.username_hint_webglobe')"
            >
                <DInput
                    id="consumer_key"
                    v-model="form.username"
                    :placeholder="__('settings.order_client.form.username')"
                    :is-valid="isFormFieldValid('username')"
                    type="text"
                />
            </DFormRow>
            <DFormRow
                :label="__('settings.order_client.form.password')"
                :is-required="true"
                :error="errorsData.password ?? errorsData['Webglobe.password']"
                :hint="__('settings.order_client.form.password_hint_webglobe')"
            >
                <DInput
                    id="consumer_secret"
                    v-model="form.password"
                    :placeholder="__('settings.order_client.form.password')"
                    :is-valid="isFormFieldValid('password')"
                    type="text"
                />
            </DFormRow>
            <DFormRow
                :label="__('settings.order_client.form.process_new_customers')"
                :error="errorsData.process_new_customers ?? errorsData['Webglobe.process_new_customers']"
                :hint="__('settings.order_client.form.process_new_customers_hint_webglobe')"
            >
                <DSwitch
                    id="process_new_customers"
                    v-model="form.process_new_customers"
                    color="success"
                    variant="3d"
                />
            </DFormRow>
            <DFormRow
                :label="__('settings.order_client.form.exclude_unknown_customers_orders')"
                :error="errorsData.exclude_unknown_customers_orders ?? errorsData['Webglobe.exclude_unknown_customers_orders']"
                :hint="__('settings.order_client.form.exclude_unknown_customers_orders_hint_webglobe')"
            >
                <DSwitch
                    id="exclude_unknown_customers_orders"
                    v-model="form.exclude_unknown_customers_orders"
                    color="success"
                    variant="3d"
                />
            </DFormRow>
            <DFormRow
                :label="__('settings.order_client.form.enable_order_confirmation_email')"
                :error="errorsData.enable_order_confirmation_email ?? errorsData['Webglobe.enable_order_confirmation_email']"
                :hint="__('settings.order_client.form.enable_order_confirmation_email_hint_webglobe')"
            >
                <DSwitch
                    id="enable_order_confirmation_email"
                    v-model="form.enable_order_confirmation_email"
                    color="success"
                    variant="3d"
                />
            </DFormRow>
            <DFormRow
                :label="__('settings.order_client.form.prepaid_invoice_behavior')"
                :error="errorsData.prepaid_invoice_behavior ?? errorsData['Webglobe.prepaid_invoice_behavior']"
                :hint="__('settings.order_client.form.prepaid_invoice_behavior_hint_webglobe')"
            >
                <DSelect
                    id="prepaid_invoice_behavior"
                    v-model="form.prepaid_invoice_behavior"
                    :options="prepaidInvoiceBehaviorTypes"
                    @update:value="updatePrepaidInvoiceBehaviorValue"
                />
            </DFormRow>
            <DRow>
                <DCol sm="4" />
                <DCol sm="4">
                    <div
                        v-if="connectionStatus === 'successful'"
                        class="green-text"
                    >
                        {{ __('settings.connections.connection_successful') }}
                    </div>
                    <div
                        v-if="connectionStatus === 'unsuccessful'"
                        class="red-text"
                    >
                        {{ __('settings.connections.connection_unsuccessful') }}
                    </div>
                </DCol>
                <DCol
                    sm="4"
                    class="check-database-connection-btn"
                >
                    <DButton
                        icon="lan-connect"
                        :label="__('settings.connections.check_connection')"
                        :disabled="isInLoadingState"
                        @click="checkConnection"
                    />
                </DCol>
                <DConnectionErrorModal
                    :show-modal="connectionException !== null"
                    :exception="connectionException"
                    @close-modal="closeErrorCheckModal"
                />
            </DRow>
        </DRow>
    </DCard>
</template>

<script setup>

import { route } from '~@/Composables/route'
import { ref, onUpdated } from 'vue'
import { useForm } from '@inertiajs/vue3'
import DConnectionErrorModal from '~@/Components/Common/DConnectionErrorModal'

const props = defineProps({
    webglobeSettings: {
        type: Object,
        default: () => { return {} }
    },
    errors: {
        type: Object,
        default: () => { return {} }
    },
    orderRecommendationLogic: {
        type: Object,
        default: () => { return {} }
    },
    recommendations: {
        type: Array,
        default: () => { return [] }
    },
    prepaidInvoiceBehaviorTypes: {
        type: Array,
        default: () => { return [] }
    }
})

const emit = defineEmits(['webglobe-valid', 'webglobe-data'])

const form = useForm({
    api_url: props.webglobeSettings.api_url ?? null,
    username: props.webglobeSettings.username,
    password: props.webglobeSettings.password,
    order_client: props.webglobeSettings.order_client ?? 'Webglobe',
    enabled: props.webglobeSettings.enabled ?? false,
    exclude_unknown_customers_orders: props.webglobeSettings.exclude_unknown_customers_orders ?? false,
    process_new_customers: props.webglobeSettings.process_new_customers ?? false,
    enable_order_confirmation_email: props.webglobeSettings.enable_order_confirmation_email ?? false,
    order_recommendation_logic: props.orderRecommendationLogic,
    prepaid_invoice_behavior: props.webglobeSettings.prepaid_invoice_behavior
})

const connectionStatus = ref('')
const apiError = ref(null)
const errorsData = ref(props.errors)
const credentialsAreValid = ref(false)
const connectionException = ref(null)

emit('webglobe-valid', !form.enabled || credentialsAreValid.value)
emit('webglobe-data', form)

const checkConnection = () => {
    errorsData.value = {}
    connectionException.value = null
    window.axios.post(route('settings.order-settings.check-webglobe', form))
        .then(function () {
            connectionStatus.value = 'successful'
            credentialsAreValid.value = true
            emit('webglobe-valid', !form.enabled || credentialsAreValid.value)
        })
        .catch(function (error) {
            connectionStatus.value = 'unsuccessful'
            credentialsAreValid.value = false
            if (error.response.data.api_error !== undefined) {
                connectionException.value = error.response.data.api_error
            }

            if (error.response.data.errors !== undefined) {
                errorsData.value = error.response.data.errors
                for (const key in errorsData.value) {
                    if (typeof errorsData.value[key] === 'object') {
                        errorsData.value[key] = errorsData.value[key][0]
                    }
                }
            }
            if (error?.response.data.exception !== undefined) {
                connectionException.value = error.response.data.exception
            }
        })
}

const closeErrorCheckModal = () => {
    apiError.value = null
    connectionException.value = null
}

onUpdated(() => {
    errorsData.value = props.errors
})

const updatePrepaidInvoiceBehaviorValue = (value) => {
    form.prepaid_invoice_behavior = value
}
</script>

<script>
export default {
    name: 'WebglobeCreate'
}
</script>

<style lang="scss" scoped>
.check-database-connection-btn {
    text-align: right;
    padding-bottom: 12px;
}
.green-text {
    color: green;
}
.red-text {
    color: red;
}
</style>
